import { useEffect, useRef, useState } from 'react';
import { t, getal } from '../i18n.js';

/**
 * Een lijngrafiek met één reeks (Umbrella: DNS-aanvragen per uur of dag). Eén as die bij 0 begint, een lijn van 2px met
 * een lichte vulling eronder, en bij de muis een verticale lijn, een punt en een tooltip met het tijdvak en het getal.
 * Twee grootheden van een andere orde (aanvragen en geblokkeerd) zijn twee grafieken onder elkaar, geen tweede as.
 *
 * Getekend op de werkelijke breedte, zoals Grafiek.jsx: met preserveAspectRatio="none" rekt de tekst uit.
 */
const tijd = (d, perDag) => new Date(d).toLocaleString('nl-NL', {
  timeZone: 'Europe/Amsterdam', ...(perDag ? { day: 'numeric', month: 'numeric' } : { hour: '2-digit', minute: '2-digit', day: 'numeric', month: 'numeric' }) });

export default function Lijngrafiek({ punten, perDag, hoogte = 200, kleur = 'var(--cyan-ink)', label, eenheid }) {
  const doos = useRef(null);
  const [W, zetW] = useState(800);
  const [tip, zetTip] = useState(null);
  useEffect(() => {
    const el = doos.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(() => zetW(Math.max(300, el.clientWidth)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const H = hoogte, padL = 52, padR = 8, padB = 22, padT = 12;
  const hoogst = Math.max(1, ...punten.map((p) => p.n));
  const macht = 10 ** Math.floor(Math.log10(hoogst));
  /* Een even top: dan valt het midden van de as op een heel getal (geen "2,5 alerts"). */
  const ruw = Math.max(2, Math.ceil(hoogst / macht) * macht);
  const top = ruw % 2 && macht === 1 ? ruw + 1 : ruw;
  const ih = H - padT - padB;
  const iw = W - padL - padR;
  const x = (i) => padL + (punten.length < 2 ? iw / 2 : (i / (punten.length - 1)) * iw);
  const y = (v) => padT + ih - (v / top) * ih;
  const lijn = punten.map((p, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(p.n).toFixed(1)}`).join('');
  const vlak = `${lijn}L${x(punten.length - 1).toFixed(1)},${y(0)}L${x(0).toFixed(1)},${y(0)}Z`;
  const elke = Math.max(1, Math.ceil(punten.length / 6));
  /* De muis kiest het dichtstbijzijnde punt; het hele vlak is het doel, niet alleen de lijn. */
  const kies = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const mx = ((e.clientX - r.left) / r.width) * W;
    const i = Math.round(((mx - padL) / iw) * (punten.length - 1));
    zetTip(Math.min(punten.length - 1, Math.max(0, i)));
  };
  const p = tip === null ? null : punten[tip];

  return (
    <div className="bigchart lijnchart" ref={doos}>
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={label}
        onMouseMove={kies} onMouseLeave={() => zetTip(null)}>
        {[0, 0.5, 1].map((f) => (
          <g key={f}>
            <line x1={padL} x2={W - padR} y1={y(top * f)} y2={y(top * f)} stroke="var(--line)" strokeWidth="1" />
            <text x={padL - 6} y={y(top * f) + 4} textAnchor="end" className="cl">{getal(top * f)}</text>
          </g>
        ))}
        {/* Het eerste label naar rechts, het laatste naar links: zo valt geen label van de rand. */}
        {punten.map((q, i) => ((i % elke === 0 && punten.length - 1 - i >= elke) || i === punten.length - 1
          ? <text key={i} x={x(i)} y={H - 5} textAnchor={i === 0 ? 'start' : i === punten.length - 1 ? 'end' : 'middle'} className="cl">{tijd(q.van, perDag)}</text>
          : null))}
        <path d={vlak} fill={kleur} opacity="0.1" />
        <path d={lijn} fill="none" stroke={kleur} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        {p && (
          <g>
            <line x1={x(tip)} x2={x(tip)} y1={padT} y2={y(0)} stroke="var(--ink-mute)" strokeWidth="1" strokeDasharray="3 3" />
            <circle cx={x(tip)} cy={y(p.n)} r="4" fill={kleur} stroke="var(--paper)" strokeWidth="2" />
          </g>
        )}
      </svg>
      {p && (
        <div className="ctip" style={{ left: `${(x(tip) / W) * 100}%` }}>
          <b>{tijd(p.van, perDag)}</b><br />{getal(p.n)} {eenheid ?? t('aanvragen')}
        </div>
      )}
    </div>
  );
}
